import React, { useEffect, useState } from 'react';
import { describeGeminiStatusShort } from '../../utils/aiPrompts';

const STEP_DEFINITIONS = [
  { id: 'scoring',       label: 'Punktevergabe',       hint: 'Bewertet jede Antwort einzeln' },
  { id: 'qualification', label: 'Qualifikationsprüfung', hint: 'Prüft formale Mindestanforderungen' },
  { id: 'analysis',      label: 'Gesamtbewertung',     hint: 'Schreibt Zusammenfassung & Stärken/Schwächen' }
];

const formatDuration = (ms) => {
  if (!Number.isFinite(ms)) return '';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const Tick = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const Dot = () => (
  <span className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
);

// Welche Modelle dieser Schritt durchprobiert hat. Ohne das sieht ein
// Durchlauf ueber die ganze Liste aus wie ein einzelner Fehlschlag — der
// Fallback laeuft, man sieht ihn nur nicht.
const AttemptTrail = ({ step, isActive }) => {
  const attempts = step.attempts || [];
  const laufend = isActive && step.currentModel && !attempts.some((a) => a.model === step.currentModel);

  if (!attempts.length && !laufend) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5">
      {attempts.map((a, i) => (
        <span
          key={`${a.model}-${i}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] ${
            a.ok
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          title={a.ok ? 'Hat geantwortet' : describeGeminiStatusShort(a.status ?? a.reason)}
        >
          {a.model}
          {!a.ok && <span className="opacity-70">{a.status ?? a.reason}</span>}
        </span>
      ))}
      {laufend && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 animate-pulse">
          {step.currentModel}…
        </span>
      )}
    </div>
  );
};

export function EvaluationProgress({ progress, title = 'KI-Bewertung läuft' }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const activeStep = progress?.steps?.find((s) => s.status === 'active');
    if (!activeStep) return undefined;
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, [progress]);

  if (!progress?.steps?.length) {
    return (
      <div className="mt-6 p-6 rounded-2xl bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <Spinner />
        <span className="text-sm">Bewertung wird vorbereitet…</span>
      </div>
    );
  }

  const totalSteps = progress.steps.length;
  const doneCount = progress.steps.filter((s) => s.status === 'done').length;
  const overallPercent = Math.round((doneCount / totalSteps) * 100);

  return (
    <div className="mt-6 p-5 sm:p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xs space-y-4 animate-fade-in">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Spinner />
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm sm:text-base">{title}</h3>
        </div>
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {doneCount} / {totalSteps}
        </span>
      </div>

      <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-green-600 dark:bg-green-500 transition-all duration-500 ease-out"
          style={{ width: `${overallPercent}%` }}
        />
      </div>

      <ol className="space-y-2">
        {progress.steps.map((step, index) => {
          const isActive = step.status === 'active';
          const isDone = step.status === 'done';
          const elapsed = isActive && step.startedAt ? now - step.startedAt : null;

          const wrapperClass = [
            'flex items-start gap-3 p-3 rounded-xl border transition-colors duration-300',
            isActive
              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
              : isDone
                ? 'bg-slate-50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-700'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 opacity-60'
          ].join(' ');

          const iconWrapClass = [
            'flex items-center justify-center w-7 h-7 rounded-full shrink-0',
            isDone
              ? 'bg-green-600 text-white'
              : isActive
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-400'
          ].join(' ');

          return (
            <li key={step.id} className={wrapperClass}>
              <span className={iconWrapClass} aria-hidden="true">
                {isDone ? <Tick /> : isActive ? <Spinner /> : <Dot />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`font-medium text-sm ${isActive ? 'text-green-800 dark:text-green-200' : 'text-slate-700 dark:text-slate-200'}`}>
                    {index + 1}. {step.label}
                  </p>
                  {isDone && (
                    <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                      {formatDuration(step.durationMs)}
                    </span>
                  )}
                  {isActive && elapsed !== null && (
                    <span className="text-xs font-mono text-green-700 dark:text-green-300">
                      {formatDuration(elapsed)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{step.hint}</p>
                <AttemptTrail step={step} isActive={isActive} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export const buildInitialProgress = () => ({
  steps: STEP_DEFINITIONS.map((s) => ({
    id: s.id,
    label: s.label,
    hint: s.hint,
    status: 'pending',
    durationMs: null,
    startedAt: null,
    attempts: [],
    currentModel: null
  }))
});

export const markStepStart = (progress, stepId) => {
  if (!progress) return progress;
  const startedAt = Date.now();
  return {
    ...progress,
    steps: progress.steps.map((s) =>
      s.id === stepId ? { ...s, status: 'active', startedAt, attempts: [], currentModel: null } : s
    )
  };
};

export const markStepEnd = (progress, stepId, durationMs) => {
  if (!progress) return progress;
  return {
    ...progress,
    steps: progress.steps.map((s) =>
      s.id === stepId ? { ...s, status: 'done', durationMs, startedAt: null } : s
    )
  };
};

/**
 * Traegt einen Modellversuch beim gerade laufenden Schritt ein. Gespeist aus
 * subscribeToAttempts, damit der Durchlauf sichtbar ist, waehrend er laeuft —
 * und nicht erst hinterher im Tagesprotokoll.
 */
export const markAttempt = (progress, event) => {
  if (!progress || !event?.model) return progress;
  const activeIndex = progress.steps.findIndex((s) => s.status === 'active');
  if (activeIndex === -1) return progress;

  return {
    ...progress,
    steps: progress.steps.map((s, i) => {
      if (i !== activeIndex) return s;
      if (event.type === 'start') return { ...s, currentModel: event.model };
      return {
        ...s,
        attempts: [
          ...(s.attempts || []),
          { model: event.model, status: event.status, reason: event.reason, ok: Boolean(event.ok) }
        ]
      };
    })
  };
};
